import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { BottomNav } from '../../components/feature/BottomNav';
import { TimelineTab } from './components/Timeline';
import { PostCard } from './components/PostCard';
import { CreatePostModal } from '../../components/feature/post/CreatePostModal';
import { useFavoriteArtists } from '../../hooks/useFavoriteArtists';
import { FilterPanel, FilterState } from './components/FilterPanel';
import { japanPrefectures } from '../../mocks/japanPrefectures';

const PAGE_SIZE = 10;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ProfileData {
  user_type: string;
  display_name: string;
  prefecture: string;
  favorite_genres: string[] | null;
  is_premium: boolean;
}

interface ArtistPost {
  id: string;
  artist_id: string;
  content: string;
  media_url: string | null;
  required_fan_level: number;
  status: string;
  created_at: string;
  profiles: {
    display_name: string;
  } | null;
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'たった今';
  if (diffMin < 60) return `${diffMin}分前`;
  if (diffHour < 24) return `${diffHour}時間前`;
  if (diffDay < 7) return `${diffDay}日前`;
  return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`rounded-2xl bg-white/5 animate-pulse ${className ?? ''}`} />;
}

function PostSkeleton() {
  return (
    <div className="bg-[#0a0a0a] rounded-2xl border border-white/5 overflow-hidden p-6 space-y-4">
      <div className="flex items-center gap-3">
        <SkeletonBlock className="w-12 h-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-3 w-20" />
        </div>
      </div>
      <SkeletonBlock className="w-full h-48" />
      <SkeletonBlock className="h-4 w-full" />
      <SkeletonBlock className="h-4 w-3/4" />
    </div>
  );
}

function parseFiltersFromParams(searchParams: URLSearchParams): FilterState {
  const genreParam = searchParams.get('genres');
  const prefParam = searchParams.get('pref');
  const kwParam = searchParams.get('q');
  const validPrefectureIds = new Set(japanPrefectures.map((p) => p.id));
  const prefectureId =
    prefParam && validPrefectureIds.has(prefParam) ? prefParam : null;
  return {
    genreIds: genreParam
      ? genreParam.split(',').filter((id) => UUID_REGEX.test(id))
      : [],
    prefectureId,
    keyword: kwParam || '',
  };
}

function parseTabFromParams(searchParams: URLSearchParams): 'global' | 'favorite' | 'unlocked' {
  const t = searchParams.get('tab');
  if (t === 'favorite' || t === 'unlocked') return t;
  return 'global';
}

function parseSortFromParams(searchParams: URLSearchParams): 'newest' | 'popular' {
  const s = searchParams.get('sort');
  if (s === 'popular') return 'popular';
  return 'newest';
}

export default function TimelinePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [activeTab, setActiveTab] = useState<'global' | 'favorite' | 'unlocked'>(
    () => parseTabFromParams(searchParams),
  );
  const [filters, setFilters] = useState<FilterState>(
    () => parseFiltersFromParams(searchParams),
  );
  const [sortOrder, setSortOrder] = useState<'newest' | 'popular'>(
    () => parseSortFromParams(searchParams),
  );

  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [posts, setPosts] = useState<ArtistPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [fanLevels, setFanLevels] = useState<Record<string, number>>({});

  const [newPostsQueue, setNewPostsQueue] = useState<ArtistPost[]>([]);
  const [showNewPostsBanner, setShowNewPostsBanner] = useState(false);

  const activeTabRef = useRef<'global' | 'favorite' | 'unlocked'>(activeTab);
  const postsRef = useRef<ArtistPost[]>([]);
  const filtersRef = useRef<FilterState>(filters);
  const sortOrderRef = useRef<'newest' | 'popular'>(sortOrder);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { favorites, loading: favoritesLoading } = useFavoriteArtists();
  // ★ state値として保持（refではなく）
  const favoriteArtistIds = favorites.map((f) => f.artist_id);
  const favoriteArtistIdsRef = useRef<string[]>(favoriteArtistIds);

  // refを最新状態に同期
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { favoriteArtistIdsRef.current = favoriteArtistIds; }, [favoriteArtistIds]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { postsRef.current = posts; }, [posts]);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);

  const updateSearchParams = useCallback(
    (
      tab: 'global' | 'favorite' | 'unlocked',
      newFilters: FilterState,
      sort: 'newest' | 'popular',
    ) => {
      const params: Record<string, string> = {};
      if (tab !== 'global') params.tab = tab;
      if (newFilters.genreIds.length > 0) params.genres = newFilters.genreIds.join(',');
      if (newFilters.prefectureId) params.pref = newFilters.prefectureId;
      if (newFilters.keyword.trim()) params.q = newFilters.keyword.trim();
      if (sort !== 'newest') params.sort = sort;
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const fetchPosts = useCallback(
    async (
      pageIndex: number,
      replace: boolean,
      tab: 'global' | 'favorite' | 'unlocked',
      favIds: string[],
      currentFilters: FilterState,
      sort: 'newest' | 'popular',
    ) => {
      // favIdsをUUIDバリデーション済みのものだけに絞る
      const validFavIds = favIds.filter((id) => UUID_REGEX.test(id));

      if (tab === 'favorite' && validFavIds.length === 0) {
        if (replace) setPosts([]);
        setHasMore(false);
        setLoadingPosts(false);
        setLoadingMore(false);
        return;
      }

      if (tab === 'unlocked' && !user) {
        if (replace) setPosts([]);
        setHasMore(false);
        setLoadingPosts(false);
        setLoadingMore(false);
        return;
      }

      if (replace) {
        setLoadingPosts(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const from = pageIndex * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        let filteredArtistIds: string[] | null = null;
        const hasGenreFilter = currentFilters.genreIds.length > 0;
        const hasPrefFilter = !!currentFilters.prefectureId;

        if (hasGenreFilter || hasPrefFilter) {
          let artistQuery = supabase
            .from('profiles')
            .select('id')
            .eq('user_type', 'maker');

          if (hasPrefFilter) {
            artistQuery = artistQuery.eq('prefecture', currentFilters.prefectureId!);
          }

          if (hasGenreFilter) {
            const validGenreIds = currentFilters.genreIds.filter((id) => UUID_REGEX.test(id));
            if (validGenreIds.length === 0) {
              if (replace) setPosts([]);
              setHasMore(false);
              setLoadingPosts(false);
              setLoadingMore(false);
              return;
            }
            artistQuery = artistQuery.overlaps('favorite_genres', validGenreIds);
          }

          const { data: artistData } = await artistQuery;
          const candidateIds = (artistData ?? [])
            .map((a: { id: string }) => a.id)
            .filter((id: string) => UUID_REGEX.test(id));

          filteredArtistIds = candidateIds;

          if (filteredArtistIds.length === 0) {
            if (replace) setPosts([]);
            setHasMore(false);
            setLoadingPosts(false);
            setLoadingMore(false);
            return;
          }
        }

        if (tab === 'unlocked' && user) {
          const { data, error } = await supabase.rpc('get_unlocked_posts', {
            p_user_id: user.id,
            p_from: from,
            p_to: to,
          });
          if (error) throw error;

          let fetched = (data ?? []).map((row: {
            id: string;
            artist_id: string;
            content: string;
            media_url: string | null;
            required_fan_level: number;
            status: string;
            created_at: string;
            display_name: string | null;
          }) => ({
            id: row.id,
            artist_id: row.artist_id,
            content: row.content,
            media_url: row.media_url,
            required_fan_level: row.required_fan_level,
            status: row.status,
            created_at: row.created_at,
            profiles: { display_name: row.display_name },
          }));

          if (filteredArtistIds !== null) {
            const idSet = new Set(filteredArtistIds);
            fetched = fetched.filter((p: ArtistPost) => idSet.has(p.artist_id));
          }
          if (currentFilters.keyword.trim()) {
            const kw = currentFilters.keyword.trim().toLowerCase();
            fetched = fetched.filter(
              (p: ArtistPost) =>
                p.content.toLowerCase().includes(kw) ||
                (p.profiles?.display_name ?? '').toLowerCase().includes(kw),
            );
          }

          if (replace) {
            setPosts(fetched);
          } else {
            setPosts((prev) => [...prev, ...fetched]);
          }
          setHasMore(fetched.length === PAGE_SIZE);
          return;
        }

        if (sort === 'popular') {
          let query = supabase
            .from('artist_posts')
            .select('*, profiles(display_name), post_echoes(count)')
            .eq('status', 'published')
            .order('created_at', { ascending: false })
            .range(from, to);

          if (tab === 'favorite') {
            query = query.in('artist_id', validFavIds);
          }
          if (filteredArtistIds !== null) {
            query = query.in('artist_id', filteredArtistIds);
          }
          if (currentFilters.keyword.trim()) {
            query = query.ilike('content', `%${currentFilters.keyword.trim()}%`);
          }

          const { data, error } = await query;
          if (error) throw error;

          const withCount = (data ?? []).map((p: ArtistPost & { post_echoes?: { count: number }[] }) => ({
            ...p,
            _echoCount: Array.isArray(p.post_echoes) ? p.post_echoes.length : 0,
          }));
          withCount.sort((a: typeof withCount[0], b: typeof withCount[0]) => b._echoCount - a._echoCount);
          const fetched = withCount.map(({ _echoCount: _ec, post_echoes: _pe, ...rest }: typeof withCount[0]) => rest);

          if (replace) {
            setPosts(fetched);
          } else {
            setPosts((prev) => [...prev, ...fetched]);
          }
          setHasMore(fetched.length === PAGE_SIZE);
          return;
        }

        // 新着順（デフォルト）
        let query = supabase
          .from('artist_posts')
          .select('*, profiles(display_name)')
          .eq('status', 'published')
          .order('created_at', { ascending: false })
          .range(from, to);

        if (tab === 'favorite') {
          query = query.in('artist_id', validFavIds);
        }
        if (filteredArtistIds !== null) {
          query = query.in('artist_id', filteredArtistIds);
        }
        if (currentFilters.keyword.trim()) {
          query = query.ilike('content', `%${currentFilters.keyword.trim()}%`);
        }

        const { data, error } = await query;
        if (error) throw error;

        const fetched = data ?? [];
        if (replace) {
          setPosts(fetched);
        } else {
          setPosts((prev) => [...prev, ...fetched]);
        }
        setHasMore(fetched.length === PAGE_SIZE);
      } catch (err) {
        console.error('Failed to fetch posts:', err);
      } finally {
        setLoadingPosts(false);
        setLoadingMore(false);
      }
    },
    [user],
  );

  // フィルター変更
  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      setPage(0);
      setHasMore(true);
      setNewPostsQueue([]);
      setShowNewPostsBanner(false);
      updateSearchParams(activeTabRef.current, newFilters, sortOrderRef.current);
      fetchPosts(0, true, activeTabRef.current, favoriteArtistIdsRef.current, newFilters, sortOrderRef.current);
    },
    [fetchPosts, updateSearchParams],
  );

  // タブ切替：fetchPostsはuseEffectに完全に委譲
  const handleTabChange = useCallback(
    (tab: 'global' | 'favorite' | 'unlocked') => {
      setActiveTab(tab);
      setPage(0);
      setHasMore(true);
      setPosts([]);
      setNewPostsQueue([]);
      setShowNewPostsBanner(false);
      updateSearchParams(tab, filtersRef.current, sortOrderRef.current);
    },
    [updateSearchParams],
  );

  // ソート変更
  const handleSortChange = useCallback(
    (sort: 'newest' | 'popular') => {
      setSortOrder(sort);
      setPage(0);
      setHasMore(true);
      setNewPostsQueue([]);
      setShowNewPostsBanner(false);
      updateSearchParams(activeTabRef.current, filtersRef.current, sort);
      fetchPosts(0, true, activeTabRef.current, favoriteArtistIdsRef.current, filtersRef.current, sort);
    },
    [fetchPosts, updateSearchParams],
  );

  // ── Supabase Realtime ──
  useEffect(() => {
    const channel = supabase
      .channel('timeline-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'artist_posts',
          filter: 'status=eq.published',
        },
        async (payload) => {
          const newRecord = payload.new as Omit<ArtistPost, 'profiles'> & { profiles?: { display_name: string } | null };

          if (user && newRecord.artist_id === user.id) return;
          if (postsRef.current.some((p) => p.id === newRecord.id)) return;

          const { data: profileData } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', newRecord.artist_id)
            .maybeSingle();

          const fullPost: ArtistPost = {
            id: newRecord.id,
            artist_id: newRecord.artist_id,
            content: newRecord.content,
            media_url: newRecord.media_url,
            required_fan_level: newRecord.required_fan_level,
            status: newRecord.status,
            created_at: newRecord.created_at,
            profiles: profileData ?? null,
          };

          const currentTab = activeTabRef.current;
          if (currentTab === 'favorite') {
            if (!favoriteArtistIdsRef.current.includes(newRecord.artist_id)) return;
          }
          if (currentTab === 'unlocked') return;

          setNewPostsQueue((prev) => {
            if (prev.some((p) => p.id === fullPost.id)) return prev;
            return [fullPost, ...prev];
          });
          setShowNewPostsBanner(true);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleLoadNewPosts = useCallback(() => {
    if (newPostsQueue.length === 0) return;
    setPosts((prev) => {
      const existingIds = new Set(prev.map((p) => p.id));
      const toAdd = newPostsQueue.filter((p) => !existingIds.has(p.id));
      return [...toAdd, ...prev];
    });
    setNewPostsQueue([]);
    setShowNewPostsBanner(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [newPostsQueue]);

  // ★ タブ切替・お気に入り読み込み完了時のデータ取得
  // favoriteArtistIds（state値）を依存に含めることで、
  // お気に入りが確定した後に正しい値でfetchが走る
  useEffect(() => {
    // favoriteタブはお気に入り読み込み完了を待つ
    if (activeTab === 'favorite' && favoritesLoading) return;

    const favIds = activeTab === 'favorite' ? favoriteArtistIds : [];
    fetchPosts(0, true, activeTab, favIds, filtersRef.current, sortOrderRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, favoritesLoading, favoriteArtistIds.join(',')]);

  // ページ追加フェッチ
  useEffect(() => {
    if (page === 0) return;
    fetchPosts(page, false, activeTabRef.current, favoriteArtistIdsRef.current, filtersRef.current, sortOrderRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Intersection Observer
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loadingPosts) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 },
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasMore, loadingMore, loadingPosts]);

  useEffect(() => {
    if (!user || posts.length === 0) return;

    const artistIds = [...new Set(posts.map((p) => p.artist_id))].filter((id) => UUID_REGEX.test(id));

    // UUIDが1件もなければスキップ
    if (artistIds.length === 0) return;

    const fetchFanLevels = async () => {
      try {
        const { data, error } = await supabase.rpc('get_user_fan_levels', {
          p_user_id: user.id,
          p_artist_ids: artistIds,
        });
        if (error) throw error;

        const levelMap: Record<string, number> = {};
        if (Array.isArray(data)) {
          data.forEach((row: { artist_id: string; fan_level: number }) => {
            levelMap[row.artist_id] = row.fan_level;
          });
        }
        setFanLevels(levelMap);
      } catch (err) {
        console.error('Failed to fetch fan levels:', err);
      }
    };

    fetchFanLevels();
  }, [user, posts]);

  useEffect(() => {
    if (!user) {
      setLoadingProfile(false);
      return;
    }

    const fetchProfile = async () => {
      const timeout = setTimeout(() => setLoadingProfile(false), 5000);
      setLoadingProfile(true);
      try {
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('user_type, display_name, prefecture, favorite_genres, is_premium')
          .eq('id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (!profileData) {
          navigate('/profile-setup');
          return;
        }
        setProfile(profileData);
      } catch (err) {
        console.error('Profile fetch failed:', err);
      } finally {
        clearTimeout(timeout);
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [user, navigate]);

  if (authLoading || (user && loadingProfile)) {
    return (
      <div>
        <div className="container mx-auto px-4 sm:px-8 pt-20 lg:pt-24 pb-8 lg:pb-12">
          <SkeletonBlock className="h-4 w-24 mb-6" />
          <SkeletonBlock className="h-12 w-48 mb-3" />
        </div>
        <div className="container mx-auto px-4 sm:px-8 space-y-6">
          <PostSkeleton />
          <PostSkeleton />
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const hasActiveFilters =
    filters.genreIds.length > 0 || !!filters.prefectureId || !!filters.keyword.trim();

  return (
    <div className="w-full min-h-screen bg-zinc-900">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-zinc-900/95 backdrop-blur-md border-b border-zinc-700/50">
        <div className="max-w-4xl mx-auto px-4 py-3 space-y-3">
          <TimelineTab
            activeTab={activeTab}
            onTabChange={handleTabChange}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
          <FilterPanel
            filters={filters}
            onChange={handleFilterChange}
            activeTab={activeTab}
          />
        </div>
      </div>

      {/* 新着投稿バナー */}
      <AnimatePresence>
        {showNewPostsBanner && newPostsQueue.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="sticky top-[105px] z-30 flex justify-center px-4 pt-3"
          >
            <motion.button
              onClick={handleLoadNewPosts}
              whileHover={{ backgroundColor: 'rgba(63,63,70,0.7)' }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2.5 px-5 py-2.5 rounded-full font-bold text-sm tracking-wide cursor-pointer whitespace-nowrap bg-zinc-700 text-zinc-100 border border-zinc-600"
              style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-brand-primary" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-primary" />
              </span>
              <i className="ri-arrow-up-line text-base" />
              <span>新着投稿 {newPostsQueue.length}件 を表示</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mx-auto max-w-4xl px-4 pt-6 pb-8">
        {loadingPosts ? (
          <div className="space-y-6">
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
          </div>
        ) : posts.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
            <i className="ri-inbox-line text-6xl text-gray-700 mb-4 block" />
            <p className="text-gray-500 text-lg tracking-wide">
              {hasActiveFilters
                ? '条件に一致する投稿が見つかりませんでした'
                : activeTab === 'unlocked'
                ? 'まだ解禁したコンテンツがありません'
                : 'まだ投稿がありません'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => handleFilterChange({ genreIds: [], prefectureId: null, keyword: '' })}
                className="mt-4 px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase cursor-pointer transition-all"
                style={{
                  background: 'rgba(20,184,166,0.15)',
                  border: '1px solid rgba(20,184,166,0.4)',
                  color: '#2dd4bf',
                }}
              >
                フィルターをクリア
              </button>
            )}
          </motion.div>
        ) : (
          <>
            <AnimatePresence>
              {(hasActiveFilters || sortOrder === 'popular') && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-2 mb-4 flex-wrap"
                >
                  {hasActiveFilters && (
                    <span
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold tracking-widest uppercase"
                      style={{
                        background: 'rgba(20,184,166,0.12)',
                        border: '1px solid rgba(20,184,166,0.3)',
                        color: '#2dd4bf',
                      }}
                    >
                      <i className="ri-filter-3-line text-xs" />
                      フィルター適用中 · {posts.length}件表示
                    </span>
                  )}
                  {sortOrder === 'popular' && (
                    <span
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold tracking-widest uppercase"
                      style={{
                        background: 'rgba(251,146,60,0.12)',
                        border: '1px solid rgba(251,146,60,0.3)',
                        color: '#fb923c',
                      }}
                    >
                      <i className="ri-fire-line text-xs" />
                      人気順で表示中
                    </span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-6">
              {posts.map((post, index) => {
                const fanLevel = fanLevels[post.artist_id] ?? 0;
                const isLocked = post.required_fan_level > fanLevel;
                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.4) }}
                  >
                    <PostCard
                      id={post.id}
                      artistId={post.artist_id}
                      artistName={post.profiles?.display_name ?? '不明なアーティスト'}
                      artistAvatar={`https://public.readdy.ai/ai/img_res/ee6cf5454a3bee42a060ef545c1f53f2.jpg${post.artist_id}`}
                      postDate={formatRelativeTime(post.created_at)}
                      imageUrl={post.media_url ?? ''}
                      content={post.content}
                      requiredFanLevel={post.required_fan_level}
                      supportTotal={0}
                      isLocked={isLocked}
                    />
                  </motion.div>
                );
              })}
            </div>

            <div ref={sentinelRef} className="h-4" />

            {loadingMore && (
              <div className="flex items-center justify-center gap-3 py-8">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-5 h-5 rounded-full border-2 border-zinc-500/30 border-t-zinc-400"
                />
                <span className="text-xs tracking-widest text-gray-500 uppercase">Loading...</span>
              </div>
            )}

            {!hasMore && posts.length > 0 && !loadingMore && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center gap-3 py-10"
              >
                <div className="h-px flex-1 bg-zinc-500/5" />
                <span className="text-[11px] tracking-[0.3em] text-zinc-600 uppercase font-bold">
                  END OF TIMELINE
                </span>
                <div className="h-px flex-1 bg-zinc-500/5" />
              </motion.div>
            )}
          </>
        )}
      </div>

      {(profile?.user_type === 'maker' || profile?.user_type === 'node') && (
        <motion.button
          onClick={() => setIsPostModalOpen(true)}
          whileHover={{ backgroundColor: profile?.user_type === 'node' ? 'rgba(34,211,238,0.9)' : 'rgba(251,113,133,0.9)' }}
          whileTap={{ scale: 0.98 }}
          className="fixed bottom-24 right-6 z-40 flex items-center gap-2.5 px-5 py-3.5 rounded-full font-bold text-sm tracking-widest uppercase cursor-pointer whitespace-nowrap text-white"
          style={{
            background: profile?.user_type === 'node' ? '#22d3ee' : '#fb7185',
            boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          }}
        >
          <i className="ri-add-line text-lg" />
          {profile?.user_type === 'node' ? '告知を投稿' : '投稿を作成'}
        </motion.button>
      )}

      <CreatePostModal
        isOpen={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
        onSuccess={() => {
          setIsPostModalOpen(false);
          setPage(0);
          setHasMore(true);
          setNewPostsQueue([]);
          setShowNewPostsBanner(false);
          fetchPosts(0, true, activeTabRef.current, favoriteArtistIdsRef.current, filtersRef.current, sortOrderRef.current);
        }}
        userType={profile?.user_type ?? 'seeker'}
      />
      <BottomNav />
    </div>
  );
}
